"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { TimeLeft } from "@/helpers/waves/time.utils";
import { calculateTimeLeft } from "@/helpers/waves/time.utils";
import { useDecisionPoints } from "@/hooks/waves/useDecisionPoints";
import { AnimatePresence } from "framer-motion";
import { TimelineToggleHeader } from "./time/TimelineToggleHeader";
import { ExpandedTimelineContent } from "./time/ExpandedTimelineContent";
import { CompactDroppingPhaseCard } from "./time/CompactDroppingPhaseCard";
import { CompactVotingPhaseCard } from "./time/CompactVotingPhaseCard";
import { useWave } from "@/hooks/useWave";
import { Time } from "@/helpers/time";
interface WaveLeaderboardTimeProps {
  readonly wave: ApiWave;
}
const AUTO_EXPAND_LIMIT = 5;
const EMPTY_TIME_LEFT: TimeLeft = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0
};
export const WaveLeaderboardTime: React.FC<WaveLeaderboardTimeProps> = t0 => {
  const $ = _c(45);
  const {
    wave
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      initialPastWindow: 4,
      initialFutureWindow: 20
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    allDecisions,
    hasMorePast,
    hasMoreFuture,
    loadMorePast,
    loadMoreFuture,
    remainingPastCount,
    remainingFutureCount
  } = useDecisionPoints(wave, t1);
  const {
    decisions: t2,
    isCurationWave,
    pauses: t3
  } = useWave(wave);
  const {
    multiDecision
  } = t2;
  const {
    showPause,
    filterDecisionsDuringPauses
  } = t3;
  const [isDecisionDetailsOpen, setIsDecisionDetailsOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(EMPTY_TIME_LEFT);
  const autoExpandFutureAttemptsRef = useRef(0);
  const [timelineFocus, setTimelineFocus] = useState(null);
  let t4;
  if ($[1] !== allDecisions || $[2] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t4 = new Set(filtered.map(_temp2));
    $[1] = allDecisions;
    $[2] = filterDecisionsDuringPauses;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const allowedDecisionTimes = t4;
  let t5;
  if ($[4] !== allDecisions || $[5] !== allowedDecisionTimes) {
    let t6;
    if ($[7] !== allowedDecisionTimes) {
      t6 = decision_0 => allowedDecisionTimes.has(decision_0.timestamp);
      $[7] = allowedDecisionTimes;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    t5 = allDecisions.filter(t6);
    $[4] = allDecisions;
    $[5] = allowedDecisionTimes;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const filteredDecisions = t5;
  let t6;
  if ($[9] !== filteredDecisions) {
    t6 = filteredDecisions.find(_temp3)?.timestamp ?? null;
    $[9] = filteredDecisions;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const nextDecisionTime = t6;
  let t7;
  let t8;
  if ($[11] !== nextDecisionTime) {
    t7 = () => {
      if (typeof nextDecisionTime !== "number") {
        setTimeLeft(EMPTY_TIME_LEFT);
        return;
      }
      const updateTimeLeft = () => {
        setTimeLeft(calculateTimeLeft(nextDecisionTime));
      };
      updateTimeLeft();
      const intervalId = globalThis.setInterval(updateTimeLeft, 1000);
      return () => {
        clearInterval(intervalId);
      };
    };
    t8 = [nextDecisionTime];
    $[11] = nextDecisionTime;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== hasMoreFuture || $[15] !== loadMoreFuture || $[16] !== nextDecisionTime) {
    t9 = () => {
      if (nextDecisionTime !== null) {
        autoExpandFutureAttemptsRef.current = 0;
        return;
      }
      if (!hasMoreFuture) {
        autoExpandFutureAttemptsRef.current = 0;
        return;
      }
      if (autoExpandFutureAttemptsRef.current >= AUTO_EXPAND_LIMIT) {
        return;
      }
      let timeoutId = null;
      let isCancelled = false;
      const tick = () => {
        if (isCancelled) {
          return;
        }
        if (autoExpandFutureAttemptsRef.current >= AUTO_EXPAND_LIMIT) {
          return;
        }
        autoExpandFutureAttemptsRef.current = autoExpandFutureAttemptsRef.current + 1;
        loadMoreFuture();
        timeoutId = globalThis.setTimeout(tick, 50);
      };
      timeoutId = globalThis.setTimeout(tick, 50);
      return () => {
        isCancelled = true;
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
      };
    };
    t10 = [nextDecisionTime, hasMoreFuture, loadMoreFuture];
    $[14] = hasMoreFuture;
    $[15] = loadMoreFuture;
    $[16] = nextDecisionTime;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  useEffect(t9, t10);
  const displayedTimeLeft = typeof nextDecisionTime === "number" ? timeLeft : EMPTY_TIME_LEFT;
  let t11;
  if ($[19] !== hasMorePast || $[20] !== loadMorePast) {
    t11 = () => {
      if (hasMorePast) {
        setTimelineFocus("start");
        loadMorePast();
      }
    };
    $[19] = hasMorePast;
    $[20] = loadMorePast;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleLoadMorePast = t11;
  let t12;
  if ($[22] !== hasMoreFuture || $[23] !== loadMoreFuture) {
    t12 = () => {
      if (hasMoreFuture) {
        setTimelineFocus("end");
        loadMoreFuture();
      }
    };
    $[22] = hasMoreFuture;
    $[23] = loadMoreFuture;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const handleLoadMoreFuture = t12;
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setTimelineFocus(null);
    };
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const onTimelineFocusHandled = t13;
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = isOpen => {
      setIsDecisionDetailsOpen(isOpen);
      if (!isOpen) {
        setTimelineFocus(null);
      }
    };
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const handleDecisionDetailsOpenChange = t14;
  let t15;
  if ($[27] !== displayedTimeLeft || $[28] !== filteredDecisions || $[29] !== handleLoadMoreFuture || $[30] !== handleLoadMorePast || $[31] !== hasMoreFuture || $[32] !== hasMorePast || $[33] !== isCurationWave || $[34] !== isDecisionDetailsOpen || $[35] !== multiDecision || $[36] !== nextDecisionTime || $[37] !== remainingFutureCount || $[38] !== remainingPastCount || $[39] !== showPause || $[40] !== timelineFocus || $[41] !== wave) {
    t15 = multiDecision ? <div className="tw-mt-2 tw-overflow-hidden tw-rounded-lg tw-bg-iron-950 md:tw-mt-4">{(() => {
        const currentPause = showPause(nextDecisionTime);
        return <TimelineToggleHeader isOpen={isDecisionDetailsOpen} setIsOpen={handleDecisionDetailsOpenChange} nextDecisionTime={nextDecisionTime} timeLeft={displayedTimeLeft} isPaused={Boolean(currentPause)} currentPause={currentPause} />;
      })()}<AnimatePresence>{isDecisionDetailsOpen && <ExpandedTimelineContent decisions={filteredDecisions} nextDecisionTime={nextDecisionTime} onLoadMorePast={hasMorePast ? handleLoadMorePast : undefined} onLoadMoreFuture={hasMoreFuture ? handleLoadMoreFuture : undefined} hasMorePast={hasMorePast} hasMoreFuture={hasMoreFuture} remainingPastCount={remainingPastCount} remainingFutureCount={remainingFutureCount} focus={timelineFocus} onFocusHandled={onTimelineFocusHandled} />}</AnimatePresence></div> : !isCurationWave && <div className="tw-overflow-hidden tw-rounded-lg tw-bg-iron-950 tw-px-3 tw-py-2"><div className="tw-flex tw-items-center tw-gap-2"><CompactDroppingPhaseCard wave={wave} /><CompactVotingPhaseCard wave={wave} /></div></div>;
    $[27] = displayedTimeLeft;
    $[28] = filteredDecisions;
    $[29] = handleLoadMoreFuture;
    $[30] = handleLoadMorePast;
    $[31] = hasMoreFuture;
    $[32] = hasMorePast;
    $[33] = isCurationWave;
    $[34] = isDecisionDetailsOpen;
    $[35] = multiDecision;
    $[36] = nextDecisionTime;
    $[37] = remainingFutureCount;
    $[38] = remainingPastCount;
    $[39] = showPause;
    $[40] = timelineFocus;
    $[41] = wave;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15) {
    t16 = <div>{t15}</div>;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
};
function _temp(decision) {
  return {
    decision_time: decision.timestamp
  };
}
function _temp2(filteredDecision) {
  return filteredDecision.decision_time;
}
function _temp3(decision_1) {
  return decision_1.timestamp > Time.currentMillis();
}